import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { deleteDrawdown, fetchDrawdownById, fetchDrawdownDealByAccounIdAndDealId, upsertFinalizedDrawdown, upsertPendingDrawdown } from './drawdown.thunks';

import { DeleteDrawdownStatus, DrawdownStatus, FetchingDealsForDrawdownStatus, IDrawdownDealItemStore, IDrawdownState, SavingDrawdownStatus } from './interfaces';
import { fetchDealsForDrawdownByAccountId } from '../distribution/deals/deals.thunk';
import { IDealForDrawdownStoreItem } from '../distribution/deals/interfaces';

const initialState: IDrawdownState = {
  loading: false,
  selectedDrawDownId: null,
  drawdown: null,
  dealsForDrawdownMap: {},
  idsOfDealsForDrawdownList: [],
  fetchingDealsForDrawdownStatus: FetchingDealsForDrawdownStatus.Idle,
  savingDrawdownStatus: SavingDrawdownStatus.Idle,
  deleteDrawdownStatus: DeleteDrawdownStatus.Idle
};

const drawdownSlice = createSlice({
  name: 'drawdown',
  initialState,
  reducers: {
    setSelectedDrawDownToEdit: (state, action: PayloadAction<string | null>) => {
      state.selectedDrawDownId = action.payload;
    },
    clearData: () => {
      return initialState;
    },
    setSelectedProjectId: (state, action: PayloadAction<string | undefined>) => {
      if(state.drawdown) {
        state.drawdown.projectId = action.payload;
      }
    },
    setDealStatus: (state, action: PayloadAction<DrawdownStatus>) => {
      if(state.drawdown) {
        state.drawdown.deal.status = action.payload;
      }
    },
    resetSavingDrawdownStatus: (state) => {
      state.savingDrawdownStatus = SavingDrawdownStatus.Idle;
    },
    resetDeleteDrawdownStatus: (state) => {
      state.deleteDrawdownStatus = DeleteDrawdownStatus.Idle;
    }
  },
  extraReducers: {
    [fetchDrawdownById?.pending.type]: (state) => {
      state.loading = true;
      state.drawdown = null;
    },
    [fetchDrawdownById?.fulfilled.type]: (state, action: PayloadAction<IDrawdownDealItemStore>) => {
      state.loading = false;
      state.drawdown = action.payload;
    },
    [fetchDrawdownById?.rejected.type]: (state) => {
      state.loading = false;
      state.drawdown = null;
    },
    [fetchDealsForDrawdownByAccountId?.pending.type]: (state) => {
      state.fetchingDealsForDrawdownStatus = FetchingDealsForDrawdownStatus.Loading;
      state.dealsForDrawdownMap = {};
      state.idsOfDealsForDrawdownList = [];
    },
    [fetchDealsForDrawdownByAccountId?.fulfilled.type]: (state, action: PayloadAction<IDealForDrawdownStoreItem[]>) => {
      state.fetchingDealsForDrawdownStatus = FetchingDealsForDrawdownStatus.Succeeded;
      const listOfIds: string[] = [];
      state.dealsForDrawdownMap = action.payload.reduce<Record<string, IDealForDrawdownStoreItem>>((acc, current) => {
        listOfIds.push(current.dealId);
        acc[current.dealId] = current;
        return acc;
      }, {});
      state.idsOfDealsForDrawdownList = listOfIds;
    },
    [fetchDealsForDrawdownByAccountId?.rejected.type]: (state) => {
      state.fetchingDealsForDrawdownStatus = FetchingDealsForDrawdownStatus.Failed;
      state.dealsForDrawdownMap = {};
      state.idsOfDealsForDrawdownList = [];
    },
    [fetchDrawdownDealByAccounIdAndDealId?.pending.type]: (state) => {
      state.loading = true;
      state.drawdown = null;
    },
    [fetchDrawdownDealByAccounIdAndDealId?.fulfilled.type]: (state, action: PayloadAction<IDrawdownDealItemStore>) => {
      state.loading = false;
      state.drawdown = action.payload;
    },
    [fetchDrawdownDealByAccounIdAndDealId?.rejected.type]: (state) => {
      state.loading = false;
      state.drawdown = null;
    },
    [upsertFinalizedDrawdown?.pending.type]: (state) => {
      state.savingDrawdownStatus = SavingDrawdownStatus.Saving;
    },
    [upsertFinalizedDrawdown?.fulfilled.type]: (state) => {
      state.savingDrawdownStatus = SavingDrawdownStatus.Succeeded;
    },
    [upsertFinalizedDrawdown?.rejected.type]: (state) => {
      state.savingDrawdownStatus = SavingDrawdownStatus.Failed;
    },
    [upsertPendingDrawdown?.pending.type]: (state) => {
      state.savingDrawdownStatus = SavingDrawdownStatus.Saving;
    },
    [upsertPendingDrawdown?.fulfilled.type]: (state) => {
      state.savingDrawdownStatus = SavingDrawdownStatus.Succeeded;
    },
    [upsertPendingDrawdown?.rejected.type]: (state) => {
      state.savingDrawdownStatus = SavingDrawdownStatus.Failed;
    },
    [deleteDrawdown?.pending.type]: (state) => {
      state.deleteDrawdownStatus = DeleteDrawdownStatus.Deleting;
    },
    [deleteDrawdown?.fulfilled.type]: (state) => {
      state.deleteDrawdownStatus = DeleteDrawdownStatus.Succeeded;
    },
    [deleteDrawdown?.rejected.type]: (state) => {
      state.deleteDrawdownStatus = DeleteDrawdownStatus.Failed;
    }
  }
});

export const actions = {
  ...drawdownSlice.actions
};

export const drawdownReducer = drawdownSlice.reducer;
